/**
 * WalkthroughTip — single positioned tooltip that spotlights a target element.
 *
 * Props:
 *   target       — ref.current DOM element to anchor to (or null → skip render)
 *   placement    — 'bottom' | 'top' | 'left' | 'right'  (default 'bottom')
 *   title        — bold line
 *   body         — supporting line
 *   step         — '1 of 3' label in the corner
 *   totalSteps   — e.g. 3
 *   onNext       — called when user clicks Next/Got it
 *   onDismiss    — called when user clicks outside the tooltip (skip all)
 *   isLast       — if true, button says "Got it" instead of "Next"
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

const ARROW_SIZE = 10;
const GAP = 14;

export const WalkthroughTip = ({
  target, placement = 'bottom', title, body,
  step, totalSteps, onNext, onDismiss, isLast,
}) => {
  const tipRef = useRef(null);
  const [pos, setPos] = useState(null);
  const [arrowPos, setArrowPos] = useState({ x: 0, y: 0 });

  useLayoutEffect(() => {
    if (!target) { setPos(null); return; }
    const rect = target.getBoundingClientRect();
    const tipEl = tipRef.current;
    if (!tipEl) return;
    const tipRect = tipEl.getBoundingClientRect();
    let x, y, ax, ay;
    const tipW = tipRect.width || 300;
    const tipH = tipRect.height || 110;

    if (placement === 'bottom') {
      x = rect.left + rect.width / 2 - tipW / 2;
      y = rect.bottom + GAP;
      ax = tipW / 2 - ARROW_SIZE / 2; ay = -ARROW_SIZE;
    } else if (placement === 'top') {
      x = rect.left + rect.width / 2 - tipW / 2;
      y = rect.top - tipH - GAP;
      ax = tipW / 2 - ARROW_SIZE / 2; ay = tipH;
    } else if (placement === 'right') {
      x = rect.right + GAP;
      y = rect.top + rect.height / 2 - tipH / 2;
      ax = -ARROW_SIZE; ay = tipH / 2 - ARROW_SIZE / 2;
    } else { // left
      x = rect.left - tipW - GAP;
      y = rect.top + rect.height / 2 - tipH / 2;
      ax = tipW; ay = tipH / 2 - ARROW_SIZE / 2;
    }

    // Keep on screen with 12px padding
    x = Math.max(12, Math.min(window.innerWidth - tipW - 12, x));
    y = Math.max(12, Math.min(window.innerHeight - tipH - 12, y));

    setPos({ x, y });
    setArrowPos({ x: ax, y: ay, placement });
  }, [target, placement, title, body]);

  // Spotlight ring — lightweight pulse around the target
  const [spotlight, setSpotlight] = useState(null);
  useLayoutEffect(() => {
    if (!target) { setSpotlight(null); return; }
    const update = () => {
      if (!target.isConnected) { setSpotlight(null); return; }
      const r = target.getBoundingClientRect();
      setSpotlight({ x: r.left - 8, y: r.top - 8, w: r.width + 16, h: r.height + 16 });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [target]);

  // Walkthrough is dismissed explicitly via the Skip button or Next button only.
  // Click-anywhere-to-dismiss conflicts with the canvas workflow (e.g. clicking
  // to pick calibration points would otherwise dismiss the step 2 tip).

  if (!target) return null;

  return (
    <>
      {/* Spotlight ring */}
      {spotlight && (
        <div
          style={{
            position: 'fixed',
            left: spotlight.x, top: spotlight.y,
            width: spotlight.w, height: spotlight.h,
            border: '2.5px solid #2563EB',
            borderRadius: 12,
            boxShadow: '0 0 0 9999px rgba(15,39,66,.32)',
            pointerEvents: 'none',
            zIndex: 9998,
            animation: 'walkthrough-pulse 1.8s ease-in-out infinite',
          }}
          data-testid="walkthrough-spotlight"
        />
      )}
      <style>{`
        @keyframes walkthrough-pulse {
          0%, 100% { box-shadow: 0 0 0 9999px rgba(15,39,66,.32), 0 0 0 0 rgba(37,99,235,.5); }
          50%      { box-shadow: 0 0 0 9999px rgba(15,39,66,.32), 0 0 0 12px rgba(37,99,235,0); }
        }
      `}</style>

      <div
        ref={tipRef}
        data-testid={`walkthrough-tip-${step}`}
        style={{
          position: 'fixed',
          left: pos?.x ?? 0,
          top: pos?.y ?? 0,
          width: 300,
          background: '#0F2742',
          color: '#fff',
          padding: '14px 16px',
          borderRadius: 10,
          boxShadow: '0 12px 36px rgba(15,39,66,.35)',
          zIndex: 9999,
          fontSize: 13,
          lineHeight: 1.5,
          visibility: pos ? 'visible' : 'hidden',
        }}
      >
        {/* Arrow */}
        <div
          style={{
            position: 'absolute',
            left: arrowPos.x, top: arrowPos.y,
            width: ARROW_SIZE, height: ARROW_SIZE,
            background: '#0F2742',
            transform: 'rotate(45deg)',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', opacity: 0.75 }}>
            Step {step} of {totalSteps}
          </div>
          <button
            onClick={onDismiss}
            data-testid={`walkthrough-skip-${step}`}
            style={{ background: 'none', border: 'none', color: '#94A3B8', fontSize: 11, cursor: 'pointer' }}
          >Skip</button>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 12, color: '#CBD5E1', marginBottom: 12 }}>{body}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onNext}
            data-testid={`walkthrough-next-${step}`}
            style={{
              background: '#fff', color: '#0F2742', border: 'none',
              padding: '7px 14px', borderRadius: 7,
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >
            {isLast ? 'Got it' : 'Next →'}
          </button>
        </div>
      </div>
    </>
  );
};

export default WalkthroughTip;
